import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Landmark, ShieldCheck, ShieldAlert, Wifi, WifiOff, Loader2, CheckCircle2, XCircle, HelpCircle, Save, Banknote } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PaymentTransactions from "@/pages/PaymentTransactions";

const PROVIDER_ICON = { bonifico: Landmark, contanti: Banknote, satispay: CreditCard, nexi: CreditCard, paypal: CreditCard };

function Level({ ok, labelOk, labelNo, neutral, neutralLabel }) {
  if (neutral) return <span className="inline-flex items-center gap-1 text-xs text-zinc-400"><HelpCircle className="h-3.5 w-3.5" /> {neutralLabel}</span>;
  return ok
    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> {labelOk}</span>
    : <span className="inline-flex items-center gap-1 text-xs text-red-400"><XCircle className="h-3.5 w-3.5" /> {labelNo}</span>;
}

function MethodCard({ m, onChanged }) {
  const Icon = PROVIDER_ICON[m.key] || CreditCard;
  const [enabled, setEnabled] = useState(m.enabled);
  const [env, setEnv] = useState(m.environment || "sandbox");
  const [iban, setIban] = useState(m.bonifico?.iban || "");
  const [intest, setIntest] = useState(m.bonifico?.intestatario || "");
  const [causale, setCausale] = useState(m.bonifico?.causale_template || "Caparra prenotazione {{numero}}");
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const isBonifico = m.key === "bonifico";
  const isContanti = m.key === "contanti";
  const isManual = m.manual || isBonifico || isContanti;

  const save = async (patch, silent) => {
    setSaving(true);
    try {
      const body = { enabled, environment: env, ...(isBonifico ? { iban, intestatario: intest, causale_template: causale } : {}), ...patch };
      const r = await api.put(`/payments/config/${m.key}`, body);
      if (!silent) toast.success("Configurazione salvata");
      onChanged?.(r.data);
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); return false; }
    finally { setSaving(false); }
  };

  const toggleEnabled = async (v) => {
    setEnabled(v);
    const ok = await save({ enabled: v }, true);
    if (ok) toast.success(v ? "Metodo abilitato" : "Metodo disabilitato");
    else setEnabled(!v);
  };

  const changeEnv = async (v) => { setEnv(v); await save({ environment: v }, true); setVerify(null); };

  const runVerify = async () => {
    setVerifying(true); setVerify(null);
    try { setVerify((await api.post(`/payments/config/${m.key}/verify`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore verifica"); }
    finally { setVerifying(false); }
  };

  return (
    <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5" data-testid={`pay-method-${m.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#f98125]/15 flex items-center justify-center"><Icon className="h-5 w-5 text-[#f98125]" /></div>
          <div>
            <div className="font-display text-xl tracking-tight leading-none">{m.nome}</div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 mt-1">{isManual ? "Manuale" : "Gateway"}{isContanti ? " · solo backoffice" : ""} · {m.supports_refund ? "rimborso supportato" : "no rimborso"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">{enabled ? "Abilitato" : "Disabilitato"}</span>
          <Switch checked={enabled} onCheckedChange={toggleEnabled} disabled={saving} data-testid={`pay-toggle-${m.key}`} />
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Ambiente</Label>
          {isManual ? (
            <div className="mt-1.5 text-sm text-zinc-500">Non applicabile (metodo manuale)</div>
          ) : (
            <Select value={env} onValueChange={changeEnv}>
              <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid={`pay-env-${m.key}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800">
                <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                <SelectItem value="production">Produzione</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Stato credenziali</Label>
          <div className="mt-2 flex flex-col gap-1.5" data-testid={`pay-cred-${m.key}`}>
            {isContanti
              ? <span className="inline-flex items-center gap-1 text-xs text-zinc-400"><HelpCircle className="h-3.5 w-3.5" /> Nessuna credenziale richiesta</span>
              : isBonifico
              ? <Level ok={!!m.bonifico?.iban} labelOk="IBAN configurato" labelNo="IBAN mancante" />
              : <>
                  <Level ok={m.credenziali_presenti} labelOk="Credenziali presenti (ENV)" labelNo="Credenziali non configurate" />
                  {!m.credenziali_presenti && m.credenziali_mancanti?.length > 0 && (
                    <div className="text-[10px] text-zinc-500 font-mono">manca: {m.credenziali_mancanti.join(", ")}</div>
                  )}
                  <Level ok={m.webhook_secret_presente} labelOk="Webhook secret presente" labelNo="Webhook secret non configurato" />
                </>}
          </div>
        </div>
      </div>

      {isBonifico && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3 border-t border-zinc-800 pt-4">
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-zinc-500">IBAN *</Label>
            <Input value={iban} onChange={(e) => setIban(e.target.value)} placeholder="IT60 X054 2811 1010 0000 0123 456" className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm font-mono" data-testid="pay-bonifico-iban" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Intestatario</Label>
            <Input value={intest} onChange={(e) => setIntest(e.target.value)} placeholder="Bicimone SRL" className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="pay-bonifico-intestatario" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Template causale</Label>
            <Input value={causale} onChange={(e) => setCausale(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="pay-bonifico-causale" /></div>
          <div className="sm:col-span-2">
            <button onClick={() => save()} disabled={saving} data-testid="pay-bonifico-save" className="rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12] disabled:opacity-60 flex items-center gap-2"><Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva dati bonifico"}</button>
            <span className="text-[11px] text-zinc-500 ml-3">Usa {"{{numero}}"} per inserire il numero prenotazione.</span>
          </div>
        </div>
      )}

      <div className="mt-4 border-t border-zinc-800 pt-4">
        {isContanti ? (
          <p className="text-[11px] text-zinc-500" data-testid="pay-contanti-note">Metodo manuale disponibile <b>solo nel backoffice</b> (mai nel portale pubblico). All'incasso, l'operatore conferma il contante dalla prenotazione: la transazione viene registrata (metodo: contanti) e confluisce in rendicontazione ed export.</p>
        ) : (
        <>
        <button onClick={runVerify} disabled={verifying} data-testid={`pay-verify-${m.key}`} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800 disabled:opacity-60 flex items-center gap-2">
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />} Verifica raggiungibilità
        </button>
        {verify && (
          <div className="mt-3 rounded-sm bg-[#0A0A0A] border border-zinc-800 p-3 space-y-2" data-testid={`pay-verify-result-${m.key}`}>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <div className="flex items-center gap-1.5 text-sm">{verify.raggiungibile ? <Wifi className="h-4 w-4 text-emerald-400" /> : <WifiOff className="h-4 w-4 text-red-400" />}<span className="text-zinc-400">Raggiungibile:</span> <b className={verify.raggiungibile ? "text-emerald-400" : "text-red-400"}>{verify.raggiungibile ? "sì" : "no"}</b></div>
              <div className="flex items-center gap-1.5 text-sm">{verify.credenziali_presenti ? <ShieldCheck className="h-4 w-4 text-emerald-400" /> : <ShieldAlert className="h-4 w-4 text-red-400" />}<span className="text-zinc-400">Credenziali presenti:</span> <b className={verify.credenziali_presenti ? "text-emerald-400" : "text-red-400"}>{verify.credenziali_presenti ? "sì" : "no"}</b></div>
              <div className="flex items-center gap-1.5 text-sm"><HelpCircle className="h-4 w-4 text-amber-400" /><span className="text-zinc-400">Credenziali valide:</span> <b className="text-amber-400">{verify.credenziali_valide === "n/a" ? "n/a" : "da verificare (P2)"}</b></div>
            </div>
            {verify.credenziali_mancanti?.length > 0 && <div className="text-[11px] text-zinc-500 font-mono">Mancano: {verify.credenziali_mancanti.join(", ")}</div>}
            {verify.dettaglio && <div className="text-[11px] text-zinc-500">{verify.dettaglio}</div>}
            <div className="text-[10px] text-zinc-600 border-t border-zinc-800 pt-2">La validità reale delle credenziali sarà verificata con un test di autenticazione quando il provider verrà integrato (P2).</div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

export default function PaymentsConfig() {
  const [methods, setMethods] = useState(null);

  const load = async () => {
    try { setMethods((await api.get("/payments/config")).data.metodi); }
    catch (e) { toast.error("Errore caricamento configurazione"); setMethods([]); }
  };
  useEffect(() => { load(); }, []);

  const onChanged = () => load();

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Amministrazione</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight flex items-center gap-3"><CreditCard className="h-8 w-8 text-[#f98125]" /> Pagamenti</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Abilita i metodi di pagamento, scegli l'ambiente e verifica la raggiungibilità dei provider. Le credenziali vengono lette dai segreti d'ambiente e non sono mai mostrate né salvate in chiaro. Un metodo disabilitato non compare mai nel checkout né nel permalink.</p>
      </div>
      {methods === null ? (
        <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#f98125] mx-auto" /></div>
      ) : (
        <Tabs defaultValue="metodi" className="w-full">
          <TabsList className="bg-[#141414] border border-zinc-800 rounded-sm mb-4">
            <TabsTrigger value="metodi" data-testid="tab-metodi">Metodi</TabsTrigger>
            <TabsTrigger value="transazioni" data-testid="tab-transazioni">Transazioni</TabsTrigger>
          </TabsList>
          <TabsContent value="metodi">
            <div className="space-y-4" data-testid="pay-methods-list">
              {methods.map((m) => <MethodCard key={m.key} m={m} onChanged={onChanged} />)}
            </div>
          </TabsContent>
          <TabsContent value="transazioni">
            <PaymentTransactions />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
